import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Info, X, ChevronDown, Users, GraduationCap, ExternalLink, BookOpen, User as UserIcon, Hash, Calendar, Clock } from 'lucide-react';

// Fase B-5E.1: nivel derivado del módulo (fuente de verdad: groups.module_slot)
function moduleLevel(m) {
  if (m == null) return null;
  if (m >= 1 && m <= 4) return 'Básico';
  if (m >= 5 && m <= 8) return 'Intermedio';
  if (m >= 9 && m <= 10) return 'Avanzado';
  return null;
}
const DAY_LABEL_CC = {
  monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', thursday: 'Jueves',
  friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo',
};
function formatDaysCC(days) {
  if (!Array.isArray(days) || days.length === 0) return null;
  const labels = days.map(d => DAY_LABEL_CC[d] || d);
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} y ${labels[1]}`;
  return labels.slice(0, -1).join(', ') + ' y ' + labels[labels.length - 1];
}


function BoardIllustration() {
  // Inline SVG matching the reference vibe: teacher pointing at a chalkboard on decorative shapes
  return (
    <svg viewBox="0 0 520 520" className="w-full max-w-md mx-auto" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="dots" x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.2" fill="#bfdbfe"/>
        </pattern>
      </defs>
      <path d="M400 60 L495 155 L390 260 L295 165 Z" fill="url(#dots)" opacity=".6"/>
      <circle cx="120" cy="380" r="130" fill="#e0e7ff" opacity=".55"/>
      <circle cx="430" cy="330" r="70" fill="#fce7f3" opacity=".7"/>
      {/* Chalkboard */}
      <rect x="200" y="180" width="230" height="150" rx="12" fill="#f5deb3"/>
      <rect x="212" y="192" width="206" height="126" rx="6" fill="#3e6b3a"/>
      <line x1="230" y1="220" x2="380" y2="220" stroke="#a3b18a" strokeWidth="2" opacity=".5"/>
      <line x1="230" y1="245" x2="360" y2="245" stroke="#a3b18a" strokeWidth="2" opacity=".5"/>
      <line x1="230" y1="270" x2="330" y2="270" stroke="#a3b18a" strokeWidth="2" opacity=".5"/>
      {/* Teacher figure */}
      <circle cx="330" cy="360" r="30" fill="#f4c2a1"/>
      <path d="M300 400 Q330 380 360 400 L370 470 Q330 490 290 470 Z" fill="#7fd0c5"/>
      <path d="M300 405 L235 350" stroke="#7fd0c5" strokeWidth="18" strokeLinecap="round"/>
      <circle cx="235" cy="348" r="6" fill="#f4c2a1"/>
      <path d="M310 335 Q330 320 350 335" stroke="#4a2f26" strokeWidth="6" fill="none" strokeLinecap="round"/>
    </svg>
  );
}

const empty = {
  name: '',
  type: 'jitsi',
  public: false,
  open_to_all: false,
  student_ids: [],
  group_ids: [],
  notify_via_mail: false,
  scheduled: false,
  scheduled_at: '',
  duration_minutes: 60,
  plan_type: 'free',
  recurrence: false,
};

export default function CreateClassroomDialog({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const canSeeGroupLink = ['super_admin', 'school_admin', 'partner_admin', 'assistant_admin'].includes(user?.role);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [invitees, setInvitees] = useState({ students: [], groups: [] });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (open) {
      setForm(empty); setQ('');
      api.get('/classrooms/invitees').then(r => setInvitees(r.data)).catch(()=>{});
    }
  }, [open]);

  const selectedStudents = useMemo(
    () => invitees.students.filter(s => form.student_ids.includes(s.id)),
    [invitees.students, form.student_ids]
  );
  const selectedGroups = useMemo(
    () => invitees.groups.filter(g => form.group_ids.includes(g.id)),
    [invitees.groups, form.group_ids]
  );
  const totalSelected = selectedStudents.length + selectedGroups.length;

  const filteredStudents = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return invitees.students;
    return invitees.students.filter(x => (x.name || '').toLowerCase().includes(s) || (x.email || '').toLowerCase().includes(s));
  }, [invitees.students, q]);
  const filteredGroups = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return invitees.groups;
    return invitees.groups.filter(g => (g.name || '').toLowerCase().includes(s));
  }, [invitees.groups, q]);

  const toggleStudent = (id) => {
    setForm(f => ({ ...f, student_ids: f.student_ids.includes(id)
      ? f.student_ids.filter(x => x !== id) : [...f.student_ids, id] }));
  };
  const toggleGroup = (id) => {
    setForm(f => ({ ...f, group_ids: f.group_ids.includes(id)
      ? f.group_ids.filter(x => x !== id) : [...f.group_ids, id] }));
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    if (!form.open_to_all && totalSelected === 0) {
      toast.error('Selecciona alumnos/grupos o activa "Abierta a todos"'); return;
    }
    if (form.scheduled && !form.scheduled_at) {
      toast.error('Elige fecha y hora para la clase programada'); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        public: form.public,
        open_to_all: form.open_to_all,
        student_ids: form.student_ids,
        group_ids: form.group_ids,
        notify_via_mail: form.notify_via_mail,
        scheduled: form.scheduled,
        scheduled_at: form.scheduled && form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
        duration_minutes: Number(form.duration_minutes) || 60,
        recurrence: form.recurrence ? 'weekly' : null,
        plan_type: form.plan_type,
      };
      const { data } = await api.post('/classrooms', payload);
      toast.success(`"${data.name}" creada · código ${data.code}`);
      onOpenChange(false);
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden" data-testid="create-classroom-dialog">
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
          <div className="text-lg font-semibold">Crear clase</div>
        </div>
        <div className="grid md:grid-cols-2 gap-6 p-6">
          {/* LEFT: Form */}
          <div className="space-y-4">
            {/* Name */}
            <div>
              <Label className="text-sm"><span className="text-rose-500 mr-1">*</span> Nombre</Label>
              <Input data-testid="cc-name" placeholder="Ej: Aperturas clásicas"
                value={form.name} onChange={(e)=>setForm(f=>({...f,name:e.target.value}))}
                className="mt-1"/>
            </div>

            {/* Type */}
            <div>
              <Label className="text-sm"><span className="text-rose-500 mr-1">*</span> Tipo</Label>
              <Select value={form.type} onValueChange={(v)=>setForm(f=>({...f,type:v}))}>
                <SelectTrigger className="mt-1" data-testid="cc-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="jitsi">Jitsi</SelectItem>
                  <SelectItem value="google_meet">Google Meet</SelectItem>
                  <SelectItem value="none">Sin videollamada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Public */}
            <div className="flex items-center gap-3">
              <Switch data-testid="cc-public" checked={form.public}
                onCheckedChange={(v)=>setForm(f=>({...f,public:v}))}/>
              <Label className="text-sm font-semibold cursor-pointer">Pública</Label>
            </div>

            {/* Open to all */}
            <div className="flex items-center gap-3">
              <Switch data-testid="cc-open-to-all" checked={form.open_to_all}
                onCheckedChange={(v)=>setForm(f=>({...f,open_to_all:v}))}/>
              <Label className="text-sm font-semibold cursor-pointer">Abierta a todos</Label>
              <span title="Cualquier alumno con el enlace o código puede unirse">
                <Info className="w-3.5 h-3.5 text-slate-400"/>
              </span>
            </div>

            {/* Students / Groups */}
            <div>
              <Label className="text-sm"><span className="text-rose-500 mr-1">*</span> Alumnos / Grupos</Label>
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <div
                    role="button"
                    tabIndex={0}
                    data-testid="cc-students-picker"
                    className="mt-1 min-h-[42px] flex flex-wrap items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm cursor-pointer hover:border-slate-300"
                  >
                    {totalSelected === 0 && <span className="text-slate-400 px-1">Alumnos y grupos</span>}
                    {selectedGroups.map(g => (
                      <span key={g.id} className="inline-flex items-center gap-1 rounded-md bg-violet-100 text-violet-700 px-2 py-0.5 text-xs">
                        <Users className="w-3 h-3"/>{g.name}
                        <button onClick={(e)=>{e.stopPropagation(); toggleGroup(g.id);}} className="hover:text-rose-500"><X className="w-3 h-3"/></button>
                      </span>
                    ))}
                    {selectedStudents.map(s => (
                      <span key={s.id} className="inline-flex items-center gap-1 rounded-md bg-blue-100 text-blue-700 px-2 py-0.5 text-xs">
                        <GraduationCap className="w-3 h-3"/>{s.name}
                        <button onClick={(e)=>{e.stopPropagation(); toggleStudent(s.id);}} className="hover:text-rose-500"><X className="w-3 h-3"/></button>
                      </span>
                    ))}
                    <span className="ml-auto text-slate-400"><ChevronDown className="w-4 h-4"/></span>
                  </div>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-[380px]">
                  <div className="p-2 border-b">
                    <Input autoFocus data-testid="cc-students-search" placeholder="Buscar alumnos o grupos…"
                      value={q} onChange={(e)=>setQ(e.target.value)} className="h-8 text-sm"/>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-1">
                    {filteredGroups.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-500 font-bold">Grupos</div>
                        {filteredGroups.map(g => (
                          <button key={g.id} type="button"
                            data-testid={`cc-group-opt-${g.id}`}
                            onClick={()=>toggleGroup(g.id)}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-slate-100 ${form.group_ids.includes(g.id) ? 'bg-violet-50 text-violet-700' : ''}`}>
                            <input type="checkbox" readOnly checked={form.group_ids.includes(g.id)}/>
                            <Users className="w-3.5 h-3.5"/> {g.name}
                          </button>
                        ))}
                      </>
                    )}
                    {filteredStudents.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Alumnos</div>
                        {filteredStudents.map(s => (
                          <button key={s.id} type="button"
                            data-testid={`cc-student-opt-${s.id}`}
                            onClick={()=>toggleStudent(s.id)}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-slate-100 ${form.student_ids.includes(s.id) ? 'bg-blue-50 text-blue-700' : ''}`}>
                            <input type="checkbox" readOnly checked={form.student_ids.includes(s.id)}/>
                            <GraduationCap className="w-3.5 h-3.5"/>
                            <div className="text-left">
                              <div>{s.name}</div>
                              <div className="text-[10px] text-slate-400 font-mono">{s.email}</div>
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                    {filteredGroups.length === 0 && filteredStudents.length === 0 && (
                      <div className="p-4 text-center text-xs text-slate-500">Sin resultados</div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Fase B-5E: Detalle visual de grupos seleccionados (Academia → Salones) */}
              {selectedGroups.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="cc-group-details-list">
                  {selectedGroups.map(g => (
                    <div key={`det-${g.id}`}
                      data-testid={`cc-group-detail-${g.id}`}
                      className="rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-2 flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm font-semibold text-violet-800 truncate">
                          <Users className="w-3.5 h-3.5 shrink-0"/>
                          {g.group_code && (
                            <span data-testid={`cc-group-code-${g.id}`}
                              className="inline-flex items-center gap-0.5 rounded bg-white/70 px-1.5 py-0.5 text-[10px] font-mono text-violet-700 border border-violet-200">
                              <Hash className="w-2.5 h-2.5"/>{g.group_code}
                            </span>
                          )}
                          <span className="truncate" data-testid={`cc-group-name-${g.id}`}>{g.name}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600">
                          <span className="inline-flex items-center gap-1" data-testid={`cc-group-module-${g.id}`}>
                            <BookOpen className="w-3 h-3 text-slate-500"/>
                            Módulo {g.module_slot ?? '—'}{moduleLevel(g.module_slot) ? ` · ${moduleLevel(g.module_slot)}` : ''}
                          </span>
                          <span className="inline-flex items-center gap-1" data-testid={`cc-group-teacher-${g.id}`}>
                            <UserIcon className="w-3 h-3 text-slate-500"/>
                            Profesor: {g.teacher_name || <span className="italic text-slate-400">sin asignar</span>}
                          </span>
                          {formatDaysCC(g.schedule_days) && (
                            <span className="inline-flex items-center gap-1" data-testid={`cc-group-days-${g.id}`}>
                              <Calendar className="w-3 h-3 text-slate-500"/>
                              {formatDaysCC(g.schedule_days)}
                            </span>
                          )}
                          {g.start_time && g.end_time && (
                            <span className="inline-flex items-center gap-1" data-testid={`cc-group-hours-${g.id}`}>
                              <Clock className="w-3 h-3 text-slate-500"/>
                              {g.start_time} – {g.end_time}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1" data-testid={`cc-group-count-${g.id}`}>
                            <GraduationCap className="w-3 h-3 text-slate-500"/>
                            {g.student_count ?? 0} alumno{(g.student_count ?? 0) === 1 ? '' : 's'} activo{(g.student_count ?? 0) === 1 ? '' : 's'}
                          </span>
                        </div>
                      </div>
                      {canSeeGroupLink && (
                        <Link to="/admin/groups"
                          data-testid={`cc-group-view-${g.id}`}
                          onClick={()=>onOpenChange(false)}
                          className="shrink-0 inline-flex items-center gap-1 rounded-md border border-violet-300 bg-white px-2 py-1 text-[11px] font-semibold text-violet-700 hover:bg-violet-100 transition">
                          Ver grupo <ExternalLink className="w-3 h-3"/>
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notify via mail */}
            <div className="flex items-center gap-3">
              <Switch data-testid="cc-notify" checked={form.notify_via_mail}
                onCheckedChange={(v)=>setForm(f=>({...f,notify_via_mail:v}))}/>
              <Label className="text-sm font-semibold cursor-pointer">Notificar por correo</Label>
            </div>

            {/* Scheduled */}
            <div>
              <div className="flex items-center gap-3">
                <Switch data-testid="cc-scheduled" checked={form.scheduled}
                  onCheckedChange={(v)=>setForm(f=>({...f,scheduled:v}))}/>
                <Label className="text-sm font-semibold cursor-pointer">Programada</Label>
              </div>
              {form.scheduled && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className="text-xs text-slate-500">Fecha y hora</Label>
                    <Input data-testid="cc-scheduled-at" type="datetime-local"
                      value={form.scheduled_at}
                      onChange={(e)=>setForm(f=>({...f,scheduled_at:e.target.value}))}/>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Duración (min)</Label>
                    <Input data-testid="cc-duration" type="number" min={5} step={5}
                      value={form.duration_minutes}
                      onChange={(e)=>setForm(f=>({...f,duration_minutes:e.target.value}))}/>
                  </div>
                </div>
              )}
            </div>

            {/* Plan Type */}
            <div>
              <Label className="text-sm"><span className="text-rose-500 mr-1">*</span> Tipo de plan</Label>
              <div className="mt-2 flex items-center gap-6 text-sm">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="plan_type" data-testid="cc-plan-free"
                    checked={form.plan_type === 'free'}
                    onChange={()=>setForm(f=>({...f,plan_type:'free'}))}
                    className="accent-indigo-600"/>
                  Gratis
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="plan_type" data-testid="cc-plan-class"
                    checked={form.plan_type === 'class-based'}
                    onChange={()=>setForm(f=>({...f,plan_type:'class-based'}))}
                    className="accent-indigo-600"/>
                  Por clase
                </label>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button data-testid="cc-start-btn" onClick={submit} disabled={saving}
                className="rounded-md bg-indigo-600 hover:bg-indigo-700 px-8">
                {saving ? 'Creando…' : 'Crear clase'}
              </Button>
            </div>
          </div>

          {/* RIGHT: Illustration */}
          <div className="hidden md:flex items-center justify-center bg-white rounded-lg">
            <BoardIllustration/>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
